import { getDealOwner, getUserIdsForTeam } from "@revelate/calc";
import {
  getCompanySellerDomains,
  normalizeSellerDomain,
} from "@revelate/constants";
import {
  NestedCompany,
  NestedCurrency,
  NestedDeal,
  NestedTeam,
} from "@revelate/types";
import { getFullName } from "@revelate/utils";
import { getDealValueInCompanyCurrency } from "../lib/currencies";
import type {
  GrowthEfficiencyDealInput,
  GrowthEfficiencyDimension,
  GrowthEfficiencyMeetingActivity,
  GrowthEfficiencyPhase,
  GrowthEfficiencyPhaseActivity,
  GrowthEfficiencyStageHistoryRecord,
} from "./growthEfficiency";

type DealEmail = {
  provider?: string | null;
  provider_id?: string | null;
  data?: {
    from?: { email?: string | null } | null;
  } | null;
  sender_email?: string | null;
  timestamp?: string | null;
};

type DealMeeting = {
  provider?: string | null;
  provider_id?: string | null;
  attendee_emails?: string | null;
  ended_at?: string | null;
  provider_data?: unknown;
  seller_participant_count?: number | string | null;
  started_at?: string | null;
};

type DealActivitySyncStatus = {
  activity_type?: string | null;
  record_count?: number | null;
  status?: string | null;
};

export type GrowthEfficiencySourceDeal = Omit<
  NestedDeal,
  "activity_sync_status" | "emails" | "meetings"
> & {
  activity_sync_status?: DealActivitySyncStatus[];
  emails?: DealEmail[];
  meetings?: DealMeeting[];
};

type DimensionMapping = Partial<Record<
  "market" | "product" | "region" | "segment" | "source",
  string
>>;

const companyDimensionMappings: Record<number, DimensionMapping> = {
  13: {
    region: "country_code",
    source: "conversion_source",
  },
  49: {
    region: "country",
    source: "source_of_deal",
  },
};


function safeObject(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return safeObject(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

export function getGrowthEfficiencyProperties(deal: GrowthEfficiencySourceDeal) {
  return safeObject(safeObject(deal.provider_data).properties);
}

function validDate(value: unknown) {
  if (typeof value !== "string" && !(value instanceof Date)) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getGrowthEfficiencyCreatedDate(deal: GrowthEfficiencySourceDeal) {
  const properties = getGrowthEfficiencyProperties(deal);
  return validDate(properties.createdate) ?? validDate(properties.hs_createdate);
}

function getGrowthEfficiencyClosedDate(deal: GrowthEfficiencySourceDeal) {
  return validDate(deal.closed_at);
}

function getCompletedActivityStatus(
  deal: GrowthEfficiencySourceDeal,
  activityType: "email" | "meeting",
) {
  return deal.activity_sync_status?.find(status => (
    status.activity_type === activityType
    && status.status === "complete"
  ));
}

function hasCompleteRelationship(
  rows: unknown[] | undefined,
  status: DealActivitySyncStatus | undefined,
) {
  if (!status) return false;
  if (!Array.isArray(rows)) return false;
  return status.record_count === null
    || status.record_count === undefined
    || status.record_count === rows.length;
}

function getMeetingTimestamps(
  deal: GrowthEfficiencySourceDeal,
  createdAt: Date | null,
  closedAt: Date | null,
) {
  const status = getCompletedActivityStatus(deal, "meeting");
  if (!hasCompleteRelationship(deal.meetings, status)) return null;
  if (!deal.meetings?.length) return [];

  const timestamps = deal.meetings.map(meeting => validDate(meeting.started_at));
  if (timestamps.some(timestamp => timestamp === null)) return null;

  return timestamps.filter((timestamp): timestamp is Date => {
    if (!timestamp) return false;
    if (createdAt && timestamp < createdAt) return false;
    if (closedAt && timestamp > closedAt) return false;
    return true;
  });
}

function getMeetingActivities(
  deal: GrowthEfficiencySourceDeal,
  companyDomains: string[],
  sellerEmails: Set<string>,
  createdAt: Date | null,
  closedAt: Date | null,
): GrowthEfficiencyMeetingActivity[] | null {
  const status = getCompletedActivityStatus(deal, "meeting");
  if (!hasCompleteRelationship(deal.meetings, status)) return null;
  if (!deal.meetings?.length) return [];

  const activities: GrowthEfficiencyMeetingActivity[] = [];
  for (const meeting of deal.meetings) {
    const timestamp = validDate(meeting.started_at);
    const endedAt = validDate(meeting.ended_at);
    if (!timestamp || !endedAt || endedAt < timestamp) return null;
    if (createdAt && timestamp < createdAt) continue;
    if (closedAt && timestamp > closedAt) continue;
    const properties = safeObject(safeObject(meeting.provider_data).properties);
    const attendeeSource = meeting.attendee_emails ?? properties.hs_all_attendee_emails;
    const attendeeEmails = typeof attendeeSource === "string"
      ? attendeeSource.split(/[;,]/).map(email => email.trim()).filter(Boolean)
      : Array.isArray(attendeeSource)
        ? attendeeSource.filter((email): email is string => typeof email === "string" && Boolean(email.trim()))
        : null;
    const storedParticipantCount = Number(meeting.seller_participant_count
      ?? properties.revelate_seller_participant_count);
    const sellerParticipantCount = Number.isFinite(storedParticipantCount)
      && storedParticipantCount >= 0
      ? Math.max(1, storedParticipantCount)
      : attendeeEmails === null
        ? null
        : Math.max(
            1,
            new Set(attendeeEmails
              .map(email => email.toLowerCase().trim())
              .filter(email => senderMatchesCompany(email, companyDomains, sellerEmails)))
              .size,
          );
    activities.push({
      durationHours: (endedAt.getTime() - timestamp.getTime()) / 3_600_000,
      sellerParticipantCount,
      timestamp,
    });
  }
  return activities;
}

export function normalizeGrowthEfficiencyCompanyDomain(companyDomain: string) {
  return normalizeSellerDomain(companyDomain);
}

function senderMatchesCompany(
  sender: string,
  companyDomains: string[],
  sellerEmails: Set<string>,
) {
  const normalizedSender = sender.toLowerCase().trim();
  if (sellerEmails.has(normalizedSender)) return true;
  const senderDomain = normalizedSender.split("@").at(-1) ?? "";
  return companyDomains.some((companyDomain) => {
    const normalizedCompanyDomain = normalizeGrowthEfficiencyCompanyDomain(companyDomain);
    return Boolean(
      normalizedCompanyDomain
      && (
        senderDomain === normalizedCompanyDomain
        || senderDomain.endsWith(`.${normalizedCompanyDomain}`)
      ),
    );
  });
}

function getSellerEmailTimestamps(
  deal: GrowthEfficiencySourceDeal,
  companyDomains: string[],
  sellerEmails: Set<string>,
  createdAt: Date | null,
  closedAt: Date | null,
) {
  const status = getCompletedActivityStatus(deal, "email");
  if (!hasCompleteRelationship(deal.emails, status)) return null;
  if (!deal.emails?.length) return [];
  if (
    !companyDomains.some(normalizeGrowthEfficiencyCompanyDomain)
    && !sellerEmails.size
  ) {
    return null;
  }

  return deal.emails.flatMap((email) => {
    const sender = email.sender_email ?? email.data?.from?.email;
    if (!sender || !senderMatchesCompany(sender, companyDomains, sellerEmails)) {
      return [];
    }
    const timestamp = validDate(email.timestamp);
    if (!timestamp) return [];
    if (createdAt && timestamp < createdAt) return [];
    if (closedAt && timestamp > closedAt) return [];
    return [timestamp];
  });
}

function getActivityPhase(
  timestamp: Date,
  createdAt: Date,
  closedAt: Date,
): GrowthEfficiencyPhase {
  const duration = Math.max(1, closedAt.getTime() - createdAt.getTime());
  const progress = (timestamp.getTime() - createdAt.getTime()) / duration;
  if (progress < 1 / 3) return "early";
  if (progress < 2 / 3) return "middle";
  return "late";
}

function buildPhaseActivity(
  meetingTimestamps: Date[] | null,
  meetingActivities: GrowthEfficiencyMeetingActivity[] | null,
  sellerEmailTimestamps: Date[] | null,
  pipelineActivityTimestamps: Date[] | null,
  createdAt: Date | null,
  closedAt: Date | null,
): GrowthEfficiencyPhaseActivity | null {
  if (
    !meetingTimestamps
    || !sellerEmailTimestamps
    || !pipelineActivityTimestamps
    || !createdAt
    || !closedAt
  ) {
    return null;
  }
  const phaseActivity: GrowthEfficiencyPhaseActivity = {
    early: { meetings: 0, sellerEmails: 0 },
    middle: { meetings: 0, sellerEmails: 0 },
    late: { meetings: 0, sellerEmails: 0 },
  };
  for (const timestamp of meetingTimestamps) {
    const phase = getActivityPhase(timestamp, createdAt, closedAt);
    phaseActivity[phase].meetings += 1;
  }
  for (const meeting of meetingActivities ?? []) {
    const phase = getActivityPhase(meeting.timestamp, createdAt, closedAt);
    phaseActivity[phase].meetingHours = (phaseActivity[phase].meetingHours ?? 0)
      + meeting.durationHours * (meeting.sellerParticipantCount ?? 1)
      + 0.5;
  }
  for (const timestamp of sellerEmailTimestamps) {
    phaseActivity[getActivityPhase(timestamp, createdAt, closedAt)].sellerEmails += 1;
  }
  const phaseWeeks: Record<GrowthEfficiencyPhase, Set<string>> = {
    early: new Set(),
    middle: new Set(),
    late: new Set(),
  };
  if (meetingActivities) {
    for (const timestamp of pipelineActivityTimestamps) {
      const phase = getActivityPhase(timestamp, createdAt, closedAt);
      const weekStart = new Date(timestamp);
      const day = (weekStart.getUTCDay() + 6) % 7;
      weekStart.setUTCDate(weekStart.getUTCDate() - day);
      weekStart.setUTCHours(0, 0, 0, 0);
      phaseWeeks[phase].add(weekStart.toISOString());
    }
    for (const phase of ["early", "middle", "late"] as const) {
      phaseActivity[phase].activeWeeks = phaseWeeks[phase].size;
    }
  }
  return phaseActivity;
}

function getPipelineActivityTimestamps(
  deal: GrowthEfficiencySourceDeal,
  meetingTimestamps: Date[] | null,
  sellerEmailTimestamps: Date[] | null,
  createdAt: Date | null,
  closedAt: Date | null,
) {
  if (!meetingTimestamps || !sellerEmailTimestamps) return null;
  const providerData = safeObject(deal.provider_data);
  const histories = safeObject(providerData.propertiesWithHistory);
  const reviewedProperties = [
    "dealstage",
    "closedate",
    "notes_last_updated",
    "notes_next_activity_date",
  ];
  const historyTimestamps = reviewedProperties.flatMap((property) => {
    const history = histories[property];
    if (!Array.isArray(history)) return [];
    return history.flatMap((record) => {
      const timestamp = validDate(safeObject(record).timestamp);
      if (!timestamp) return [];
      if (createdAt && timestamp < createdAt) return [];
      if (closedAt && timestamp > closedAt) return [];
      return [timestamp];
    });
  });
  return [...meetingTimestamps, ...sellerEmailTimestamps, ...historyTimestamps];
}

function getStageHistory(
  deal: GrowthEfficiencySourceDeal,
  createdAt: Date | null,
  closedAt: Date | null,
): GrowthEfficiencyStageHistoryRecord[] | null {
  const histories = safeObject(
    safeObject(deal.provider_data).propertiesWithHistory,
  );
  const history = histories.dealstage;
  if (!Array.isArray(history) || !history.length) return null;
  const records = history.flatMap((record) => {
    const row = safeObject(record);
    const timestamp = validDate(row.timestamp);
    const stageId = typeof row.value === "string" ? row.value.trim() : "";
    if (!timestamp || !stageId) return [];
    if (closedAt && timestamp > closedAt) return [];
    return [{ stageId, timestamp }];
  }).toSorted((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  if (!records.length) return null;
  if (createdAt && records.every(record => record.timestamp < createdAt)) {
    return null;
  }
  return records;
}

function dimensionValue(properties: Record<string, unknown>, key?: string) {
  if (!key) return undefined;
  const value = properties[key];
  return typeof value === "string" && value.trim()
    ? value.trim()
    : undefined;
}

export function normalizeGrowthEfficiencyRegion(value?: string) {
  if (!value?.trim()) return undefined;
  const regions = value
    .split(";")
    .map(region => region.trim())
    .filter(Boolean);
  if (!regions.length) return undefined;
  return regions.length > 1 ? "Multiple regions" : regions[0];
}

function getDealTeam(
  deal: GrowthEfficiencySourceDeal,
  teams: NestedTeam[],
) {
  const ownerId = getDealOwner(deal as NestedDeal)?.id;
  if (!ownerId) return undefined;
  return teams
    .toSorted((a, b) => a.name.localeCompare(b.name))
    .find(team => getUserIdsForTeam(teams, team.id, true).includes(ownerId))
    ?.name;
}

function getPipelineLabel(
  pipelineId: string,
  pipelines: Array<{ id: string; label: string }>,
) {
  return pipelines.find(pipeline => pipeline.id === pipelineId)?.label
    ?? (pipelineId || "Not specified");
}

export function adaptGrowthEfficiencyDeal(
  deal: GrowthEfficiencySourceDeal,
  context: {
    company: NestedCompany;
    currencies: NestedCurrency[];
    pipelines: Array<{ id: string; label: string }>;
    teams: NestedTeam[];
  },
  options: { activityTo?: Date; requireVerifiedRelationships?: boolean } = {},
): GrowthEfficiencyDealInput {
  // Parse the large history payload once per adaptation. Helpers accept object or
  // string provider data; the original shared source record remains untouched.
  deal = {
    ...deal,
    provider_data: safeObject(deal.provider_data) as GrowthEfficiencySourceDeal["provider_data"],
  };
  if (options.requireVerifiedRelationships) {
    const verified = new Set<string>();
    for (const type of ["email", "meeting"] as const) {
      const rows = type === "email" ? deal.emails : deal.meetings;
      const states = deal.activity_sync_status?.filter(state => state.activity_type === type) ?? [];
      const state = states[0];
      const identities = rows?.map(row => row.provider_id);
      const validCount = typeof state?.record_count === "number" && Number.isInteger(state.record_count) && state.record_count >= 0;
      const validIdentities = typeof deal.provider === "string" && deal.provider.trim().length > 0
        && rows?.every(row => row.provider === deal.provider && typeof row.provider_id === "string" && row.provider_id.trim().length > 0)
        && new Set(identities).size === rows.length;
      const knownSenders = type !== "email" || deal.emails?.every(email => {
        const sender = email.sender_email ?? email.data?.from?.email;
        return typeof sender === "string" && sender.trim().length > 0;
      });
      if (states.length === 1 && state?.status === "complete" && Array.isArray(rows)
        && validCount && state.record_count === rows.length && validIdentities && knownSenders) verified.add(type);
    }
    // This stricter V2 boundary leaves legacy callers and source records intact.
    deal = { ...deal, activity_sync_status: deal.activity_sync_status?.filter(state =>
      !["email", "meeting"].includes(state.activity_type ?? "") || verified.has(state.activity_type ?? "")) };
  }
  const properties = getGrowthEfficiencyProperties(deal);
  const mapping = companyDimensionMappings[context.company.id] ?? {};
  const createdAt = getGrowthEfficiencyCreatedDate(deal);
  const closedAt = getGrowthEfficiencyClosedDate(deal);
  const owner = getDealOwner(deal as NestedDeal);
  const pipelineId = String(deal.pipeline ?? "not-specified");
  const activityTo = closedAt ?? options.activityTo ?? null;
  const sellerEmails = new Set(
    (context.company.users ?? [])
      .map(user => user.email?.toLowerCase().trim())
      .filter((email): email is string => Boolean(email)),
  );
  const companyDomains = getCompanySellerDomains(
    context.company.id,
    context.company.domain,
  );
  const meetingTimestamps = getMeetingTimestamps(deal, createdAt, activityTo);
  const meetingActivities = getMeetingActivities(
    deal,
    companyDomains,
    sellerEmails,
    createdAt,
    activityTo,
  );
  const sellerEmailTimestamps = getSellerEmailTimestamps(
    deal,
    companyDomains,
    sellerEmails,
    createdAt,
    activityTo,
  );
  const pipelineActivityTimestamps = getPipelineActivityTimestamps(
    deal,
    meetingTimestamps,
    sellerEmailTimestamps,
    createdAt,
    activityTo,
  );

  return {
    closedAt,
    createdAt,
    dimensions: {
      market: dimensionValue(properties, mapping.market),
      pipeline: getPipelineLabel(pipelineId, context.pipelines),
      product: dimensionValue(properties, mapping.product),
      region: normalizeGrowthEfficiencyRegion(
        dimensionValue(properties, mapping.region),
      ),
      sales_rep: owner ? getFullName(owner) : "Not specified",
      segment: dimensionValue(properties, mapping.segment),
      source: dimensionValue(properties, mapping.source),
      team: getDealTeam(deal, context.teams) ?? "Not specified",
    },
    id: deal.id,
    isLost: Boolean(deal.is_lost),
    isWon: Boolean(deal.is_won),
    name: deal.name,
    meetingActivities,
    meetingTimestamps,
    meetings: meetingTimestamps?.length ?? null,
    phaseActivity: buildPhaseActivity(
      meetingTimestamps,
      meetingActivities,
      sellerEmailTimestamps,
      pipelineActivityTimestamps,
      createdAt,
      closedAt,
    ),
    pipelineActivityTimestamps,
    pipelineId,
    sellerEmails: sellerEmailTimestamps?.length ?? null,
    sellerEmailTimestamps,
    stageHistory: getStageHistory(deal, createdAt, closedAt),
    value: getDealValueInCompanyCurrency(
      deal as NestedDeal,
      context.currencies,
      context.company,
    ),
  };
}

/** Request-local adapter cache; never shared between companies or data refreshes. */
export function createGrowthEfficiencyDealAdapter(
  context: Parameters<typeof adaptGrowthEfficiencyDeal>[1],
  options: Parameters<typeof adaptGrowthEfficiencyDeal>[2] = {},
) {
  const cache = new WeakMap<GrowthEfficiencySourceDeal, GrowthEfficiencyDealInput>();
  return (deal: GrowthEfficiencySourceDeal) => {
    const existing = cache.get(deal);
    if (existing) return existing;
    const adapted = adaptGrowthEfficiencyDeal(deal, context, options);
    cache.set(deal, adapted);
    return adapted;
  };
}

export function filterGrowthEfficiencySourceDeals(
  deals: GrowthEfficiencySourceDeal[],
  teams: NestedTeam[],
  filters: {
    owners: string[];
    pipelines: string[];
    teams: string[];
  },
) {
  const selectedTeamOwnerIds = new Set(
    filters.teams.flatMap(teamId => getUserIdsForTeam(
      teams,
      Number(teamId),
      true,
    )),
  );

  return deals.filter((deal) => {
    const pipelineId = String(deal.pipeline ?? "not-specified");
    if (filters.pipelines.length && !filters.pipelines.includes(pipelineId)) {
      return false;
    }
    const ownerId = getDealOwner(deal as NestedDeal)?.id;
    if (filters.teams.length && (!ownerId || !selectedTeamOwnerIds.has(ownerId))) {
      return false;
    }
    if (filters.owners.length && (!ownerId || !filters.owners.includes(ownerId))) {
      return false;
    }
    return true;
  });
}

export function getAvailableGrowthEfficiencyDimensions(
  deals: GrowthEfficiencyDealInput[],
  range?: { from: Date; to: Date },
) {
  const ordered: Array<{ key: GrowthEfficiencyDimension; label: string }> = [
    { key: "team", label: "Team" },
    { key: "sales_rep", label: "Sales rep" },
    { key: "pipeline", label: "Pipeline" },
    { key: "segment", label: "Segment" },
    { key: "region", label: "Region" },
    { key: "source", label: "Source" },
    { key: "market", label: "Market" },
    { key: "product", label: "Product" },
  ];

  const population = range
    ? deals.filter(deal => (
        deal.closedAt
        && deal.closedAt >= range.from
        && deal.closedAt <= range.to
      ))
    : deals;

  return ordered.map(dimension => ({
    ...dimension,
    disabled: !population.some((deal) => {
      const value = deal.dimensions[dimension.key];
      return value && value !== "Not specified";
    }),
  }));
}

export function resolveGrowthEfficiencyBreakdownDimension(
  requested: GrowthEfficiencyDimension,
  dimensions: Array<{
    disabled: boolean;
    key: GrowthEfficiencyDimension;
  }>,
) {
  const requestedDimension = dimensions.find(
    dimension => dimension.key === requested,
  );
  if (requestedDimension && !requestedDimension.disabled) return requested;
  return dimensions.find(dimension => !dimension.disabled)?.key ?? null;
}
